import { getSelectedPropertyId } from '@/auth/selectedPropertyStore';
import type { ApiSuccessEnvelope } from '@/api/types';

// v2: only collection ("long list") GETs are cached now — bumping the key
// discards any v1 single-record entries whose stale updated_at was driving
// spurious "record changed while offline" 409s.
const STORAGE_KEY = 'nangbook-offline-api-cache-v2';
const MAX_CACHE_ENTRIES = 160;
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CACHE_USED_EVENT = 'nangbook-offline-cache-used';

let userScope: string | null = null;
let lastCacheUse: OfflineCacheUse | null = null;

interface CacheEntry {
  key: string;
  path: string;
  propertyId: number | null;
  userScope: string;
  cachedAt: string;
  envelope: ApiSuccessEnvelope<unknown>;
}

export interface OfflineCacheUse {
  path: string;
  cachedAt: string;
  usedAt: string;
}

export function setOfflineCacheUserScope(userId: number | null): void {
  userScope = userId === null ? null : String(userId);
}

export function shouldCacheApiGet(path: string): boolean {
  const cleanPath = path.split('?')[0] ?? path;

  if (
    cleanPath.startsWith('/auth/')
    || cleanPath.startsWith('/offline-sync-operations')
    || cleanPath.includes('/export')
    || cleanPath.endsWith('/file')
    || cleanPath.startsWith('/public/')
  ) {
    return false;
  }

  // Only cache "long list" (collection) endpoints for offline browsing — never
  // individual records. A single-resource path carries a numeric id segment
  // (e.g. /bookings/70, /bookings/70/payments/5); a collection does not
  // (e.g. /bookings, /guests). Not caching single records means no per-record
  // updated_at is ever stored, so the optimistic-concurrency precondition is
  // never sent for one — which is what raised spurious "record changed" 409s
  // while online.
  const segments = cleanPath.split('/').filter(Boolean);

  return segments.length > 0 && !segments.some((segment) => /^\d+$/.test(segment));
}

export function writeCachedApiEnvelope<TData>(path: string, envelope: ApiSuccessEnvelope<TData>): void {
  if (typeof window === 'undefined' || !shouldCacheApiGet(path)) return;

  const key = cacheKey(path);
  const entries = readEntries().filter((entry) => entry.key !== key);

  entries.unshift({
    key,
    path,
    propertyId: getSelectedPropertyId(),
    userScope: userScope ?? 'anonymous',
    cachedAt: new Date().toISOString(),
    envelope: envelope as ApiSuccessEnvelope<unknown>,
  });

  writeEntries(entries.slice(0, MAX_CACHE_ENTRIES));
}

export function readCachedApiEnvelope<TData>(path: string): ApiSuccessEnvelope<TData> | null {
  if (typeof window === 'undefined' || !shouldCacheApiGet(path)) return null;

  const key = cacheKey(path);
  const entry = readEntries().find((candidate) => candidate.key === key);

  if (!entry || Date.now() - Date.parse(entry.cachedAt) > CACHE_TTL_MS) {
    return null;
  }

  lastCacheUse = { path, cachedAt: entry.cachedAt, usedAt: new Date().toISOString() };
  window.dispatchEvent(new CustomEvent(CACHE_USED_EVENT, { detail: lastCacheUse }));

  return entry.envelope as ApiSuccessEnvelope<TData>;
}

export function findCachedResourceUpdatedAt(path: string): string | null {
  // Single records are no longer cached, so there is never a resource-version
  // precondition to send for them (guards against any lingering entry too).
  if (!shouldCacheApiGet(path)) return null;

  const envelope = readCachedEnvelopeWithoutNotifying(path);
  const data = envelope?.data;

  if (data && typeof data === 'object' && 'updated_at' in data && typeof data.updated_at === 'string') {
    return data.updated_at;
  }

  return null;
}

export function getLastOfflineCacheUse(): OfflineCacheUse | null {
  return lastCacheUse;
}

export function subscribeToOfflineCacheUse(callback: (cacheUse: OfflineCacheUse) => void): () => void {
  function handleCacheUse(event: Event) {
    callback((event as CustomEvent<OfflineCacheUse>).detail);
  }

  window.addEventListener(CACHE_USED_EVENT, handleCacheUse);

  return () => window.removeEventListener(CACHE_USED_EVENT, handleCacheUse);
}

function cacheKey(path: string): string {
  return [userScope ?? 'anonymous', getSelectedPropertyId() ?? 'none', path].join('|');
}

function readCachedEnvelopeWithoutNotifying(path: string): ApiSuccessEnvelope<unknown> | null {
  const key = cacheKey(path);
  const entry = readEntries().find((candidate) => candidate.key === key);

  if (!entry || Date.now() - Date.parse(entry.cachedAt) > CACHE_TTL_MS) {
    return null;
  }

  return entry.envelope;
}

function readEntries(): CacheEntry[] {
  if (typeof window === 'undefined') return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isCacheEntry) : [];
  } catch {
    return [];
  }
}

function writeEntries(entries: CacheEntry[]): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

function isCacheEntry(value: unknown): value is CacheEntry {
  return Boolean(
    value
      && typeof value === 'object'
      && 'key' in value
      && 'path' in value
      && 'cachedAt' in value
      && 'envelope' in value
  );
}
